import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Card, CardContent } from "./common/card";
import { FaSeedling, FaBrain, FaChartLine, FaGamepad, FaTrophy, FaFistRaised, FaMedal,
  FaDiscord, FaTwitch, FaGithub, FaEnvelope
} from "react-icons/fa";
import SeedVerse from "./SeedVerse";


const Home = () => {
  // todo disable animation once it is done (to remove lag)
  // disable it altogether when we are already logged in ?
  const [headLine, setHeadLine] = useState('');
  const [isCursorVisible, setIsCursorVisible] = useState(false); // State for the cursor visibility
  const [isAnimationDone, setIsAnimationDone] = useState(false);
  const fullText = 'A New Seed, A New Champion.';

  useEffect(() => {
    // Check if the user directly landed on the site (first visit)
    const isFirstVisit = window.history.length <= 2;
    //console.log(window.history.length)

    if (!isFirstVisit) {
      setHeadLine(fullText);
      setIsAnimationDone(true);
      setIsCursorVisible(false);

      return;
    }

    // Delay the animation start by 1 second (1000ms)
    const startAnimationAfterDelay = setTimeout(() => {

      let index = 0;
      let typingText = '';
      setHeadLine('');

      const typeText = () => {
        if (index < fullText.length) {
          typingText += fullText[index];
          setHeadLine(typingText);
          index++;

          if (fullText[index - 1] === ',') {
            setTimeout(typeText, 1000);
          } else {
            setTimeout(typeText, 75); // Normal typing speed
          }
        } else {
          //setIsAnimationDone(true); // Mark animation as complete
        }
      };

      typeText();
    }, 1000); // Wait 1 second before starting the animation

    return () => clearTimeout(startAnimationAfterDelay); // Cleanup on unmount
  }, []);

  // Cursor blinking effect
  useEffect(() => {
    if (isAnimationDone) return; // Stop cursor blinking when animation is done for a while

    const cursorInterval = setInterval(() => {
      setIsCursorVisible((prev) => !prev);
    }, 500);

    const stopCursorBlink = setTimeout(() => {
      clearInterval(cursorInterval);
      setIsCursorVisible(false);
      setIsAnimationDone(true);
    }, 10000);

    return () => {
      clearInterval(cursorInterval);
      clearTimeout(stopCursorBlink);
    };
  }, [isAnimationDone]);

  return (
    <div className="max-w-7xl mx-auto lg:py-8 text-center space-y-10">
      {/* SEO Metadata */}
      <Helmet>
        <title>SeedSmash - Compete With Your Own Super Smash Bros Melee Reinforcement Learning bots</title>
        <meta 
          name="description" 
          content="SeedSmash lets you create, customize, and compete with Super Smash Bros. Melee Reinforcement AIs. Create melee bots, watch live training progess, battles,track stats and more." 
        />
        <meta name="keywords" content="SeedSmash, AI, Super Smash Bros, Melee, machine learning, bot, SS, phillip, xpilot, slippi, smash, smashladder, twitch, reinforcement learning, Twitch, gaming, AI bots, SSBM"/>
        <meta property="og:title" content="SeedSmash - Compete With Your Own Super Smash Bros Melee Reinforcement Learning bots"/>
        <meta property="og:description" content="Train your own Smash bot, compete, and analyze advanced stats in this AI-driven Melee competition!"/>
        <meta property="og:image" content="/logo512.webp"/> 
        <meta property="og:type" content="website"/>
      </Helmet>

      <div className="flex flex-col items-center min-h-screen p-1 space-y-12 py-6">
        <h1 className="tracking-wide rounded-xl text-white p-1 drop-shadow-lg flex items-center justify-center">
          <span className="text-md lg:text-5xl leading-none">「</span>
          <span className="text-lg lg:text-5xl lg:pl-12">{headLine}<span className={`cursor ${isCursorVisible ? 'opacity-100' : 'opacity-0'}`}>▍</span></span>
          <span className="text-md lg:text-5xl leading-none">」</span>
        </h1>

        <div className="grid grid-cols-2 gap-8 max-w-lg lg:max-w-2xl relative">
          <div className=" justify-center w-45 lg:w-80 mx-auto backdrop-blur-md rounded-xl">
            <FeatureCard icon={FaSeedling} title="Create" description="Create and design your own melee AI, from its core to playstyle preferences." />
          </div>
          <div className="flex justify-end w-45 lg:w-80 mx-auto backdrop-blur-md rounded-xl">
            <FeatureCard icon={FaBrain} title="Train" description="Your AI will learn by facing other AIs of similar level." />
          </div>
          <div className="flex justify-start w-45 lg:w-80 mx-auto backdrop-blur-md rounded-xl">
            <FeatureCard icon={FaChartLine} title="Analyze" description="Live Twitch streams, detailed stats, and progress tracking for your AI." />
          </div>
          <div className="justify-center w-45 lg:w-80 mx-auto backdrop-blur-md rounded-xl">
            <FeatureCard icon={FaMedal} title="Rank Up" description="Reiterate, fine-tune, inovate and stack up on the ladder!" />
          </div>
        </div>

        <div className="text-center space-y-6 text-xs lg:text-md">
          <h2 className="text-2xl lg:text-4xl font-bold">What's SeedSmash?</h2>
          <div className="grid grid-cols-1 grid-cols-2 gap-6 max-w-xl mx-auto text-left">
            
            <div className="text-cyan-100 text-xl text-center">1. Fast Parallel Training Made for Viewers</div>
            <p className="text-gray-200/80">
              SeedSmash simulates many matches in parallel at high speed, accumulating 
              around 3 hours of real gameplay each minute.
            </p>

            <div className="text-cyan-100 text-xl text-center">2. Your Seed Defines Your AI</div>
            <p className="text-gray-200/80">
              Each AI evolves over time, adapting based on the strategy it was given. 
              A weak seed leads to poor performance, but strong strategies can emerge after a couple days of training.
            </p>

            <div className="text-cyan-100 text-xl font-semibold text-center">
              4. A Passion-Driven Experiment
            </div>
            <p className="text-gray-200/80">
              SeedSmash is an independent project. It comes with strong resource limitations.
            </p>
            <div className="text-white border border-white/60 w-2/3 mx-auto col-span-2 rounded-lg p-2 space-y-2">
              <p>• Maximum <span className="underline">15 live bots</span> at a time.</p>
              <p>• Limited to <span className="underline">one bot per user</span>.</p>
              <p>• The <span className="underline">lowest-performing bot</span> is replaced daily by the next in the queue.</p>
            </div>


          </div>
        </div>



        <div className="text-center space-y-4">
          <h2 className="text-3xl ">Coming Next.</h2>
          <ul className="text-gray-200/80 text-lg space-y-2">
            <li className="flex items-center space-x-8 justify-start">
              <img src="/misc/SLP.svg" alt="" className="w-6 h-6" />
              <span>Replays</span>
            </li>
            <li className="flex items-center space-x-8 justify-start">
              <FaTrophy className="text-white text-xl" />
              <span>AI Tournaments</span>
            </li>
            <li className="flex items-center space-x-8 justify-start">
              <FaGamepad className="text-white text-xl" />
              <span>Play against SeedSmash AIs</span>
            </li>
            <li className="flex items-center space-x-8 justify-start">
              <FaFistRaised className="text-white text-xl" />
              <span>The SeedSmash Gauntlet</span>
            </li>
          </ul>
        </div>

        <footer className="text-center text-gray-400 py-6 mt-12">
          <div className="space-x-6 flex mx-auto justify-center pb-8">
            <a href="https://discord.com" target="_blank" rel="noopener noreferrer">
             <FaDiscord className="text-white text-3xl hover:text-cyan-100 transition-colors" />
            </a>
            <a href="https://twitch.tv/seedsmash" target="_blank" rel="noopener noreferrer">
              <FaTwitch className="text-white text-3xl hover:text-cyan-100 transition-colors" />
            </a>
            <a href="https://github.com/villinvic/seedsmash/tree/main" target="_blank" rel="noopener noreferrer">
              <FaGithub className="text-white text-3xl hover:text-cyan-100 transition-colors" />
            </a>
            <a href="mailto:goji@seedsmash.ai" className="">
              <FaEnvelope className="text-white text-3xl hover:text-cyan-100 transition-colors" />
            </a>
          </div>
          <SeedVerse/>
          <p className="mt-4 text-xs text-gray-400/50">© 2025 SeedSmash. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default Home;

function FeatureCard({ icon: Icon, title, description }) {
  return (
    <div>
      <Card className="p-2">
        <CardContent className="space-y-4">
          <Icon className="text-2xl lg:text-5xl mx-auto" />
          <h3 className="text-md lg:text-3xl">{title}</h3>
          <p className="mt-2 text-xs lg:text-sm text-center text-cyan-100/80">{description}</p>
        </CardContent>
      </Card>
    </div>
  );
}

